/**
 * WorkspaceSearch: the Workspace band's search box, the house SearchField
 * at its compact size, with a results panel under it.
 *
 * It finds clients, tasks and people:
 *   • clients + intakes: a debounced SERVER search (listWorkItems `q`, which
 *     matches prospect/caller/converted-client names and now titles) across
 *     the whole workspace, open AND closed;
 *   • tasks: the same server search matches task titles;
 *   • people: the workspace member list, filtered by name client-side.
 * Results render in a dropdown; picking one navigates to its home (a task
 * opens on My Tasks, an active intake on the Intake Pipeline, a closed one on
 * Completed, a person filters the Caseload view).
 *
 * The box ALSO keeps its original pass-through role: `value`/`onChange` feed
 * the per-view filters in Workspace.tsx unchanged (client-side for the active
 * views, server-side for Completed; ADR-015 two-engine split). The dropdown is
 * a third, view-independent engine layered on top, not a replacement.
 *
 * Typing opens the panel. Emptying the field, by its clear button or by
 * deleting the text, closes the panel at once, without waiting for the
 * debounce. Escape closes the panel and, through SearchField, empties the
 * field in the same press.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { useWorkItems, useWorkspaceMembers } from '@/lib/workspace';
import type { WorkItemRow } from '@/lib/api';
import { SearchField } from '@/components/primitives/SearchField';

const MIN_CHARS = 2;

function itemName(r: WorkItemRow): string {
  return r.display_name || r.prospect_name || r.title || 'Untitled';
}

/** Where a result lives: a task on My Tasks, a closed intake on Completed,
 *  an active intake on the pipeline board. The drawer opens via ?item=. */
function itemHref(r: WorkItemRow): string {
  if (r.kind === 'task') return `?view=mytasks&item=${r.id}`;
  if (r.closed_at) return `?view=completed&item=${r.id}`;
  return `?view=pipeline&item=${r.id}`;
}

function itemContext(r: WorkItemRow): string {
  if (r.kind === 'task') return 'Task';
  if (r.closed_at) return 'Closed';
  return r.current_stage_label ?? 'Intake';
}

export interface WorkspaceSearchProps {
  value: string;
  onChange: (next: string) => void;
}

export function WorkspaceSearch({ value, onChange }: WorkspaceSearchProps): ReactNode {
  const { tenantSlug } = useTenant();
  const [open, setOpen] = useState(false);

  // Debounce the term into the server request (no request per keystroke).
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), 250);
    return () => clearTimeout(t);
  }, [value]);

  const active = debounced.length >= MIN_CHARS;
  // Whole-workspace scope (open + closed; archived stays out) so a client the
  // caller KNOWS exists always fires, whatever view the box sits on.
  const resultsQ = useWorkItems(
    tenantSlug,
    { q: debounced, include_closed: true, limit: 8 },
    { enabled: active },
  );
  const membersQ = useWorkspaceMembers(tenantSlug, { enabled: active });

  const tasks = active ? resultsQ.rows.filter((r) => r.kind === 'task').slice(0, 4) : [];
  const cases = active ? resultsQ.rows.filter((r) => r.kind === 'intake').slice(0, 5) : [];
  const people = active
    ? membersQ.members.filter((m) => m.name.toLowerCase().includes(debounced.toLowerCase())).slice(0, 3)
    : [];
  const total = tasks.length + cases.length + people.length;
  const showPanel = open && active;

  const sectionHead = 'px-3 pt-2 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-sage-400';
  const rowClass =
    'flex w-full items-baseline gap-2 px-3 py-1.5 text-[13px] text-ink hover:bg-sage-50 focus-visible:outline-none focus-visible:bg-sage-50';

  return (
    <div className="relative w-60">
      <SearchField
        value={value}
        onChange={(next) => {
          onChange(next);
          setOpen(next !== '');
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        placeholder="Search the workspace"
        aria-label="Search the workspace"
        aria-expanded={showPanel}
        size="compact"
        testId="workspace-search"
      />

      {showPanel && (
        <div
          data-testid="workspace-search-results"
          className="absolute left-0 right-0 top-full z-30 mt-1.5 max-h-80 overflow-y-auto rounded-base border border-sage-300 bg-paper py-1 shadow-sh1"
        >
          {cases.length > 0 && (
            <div data-testid="workspace-search-clients">
              <div className={sectionHead}>Clients &amp; intakes</div>
              {cases.map((r) => (
                <Link
                  key={r.id}
                  to={itemHref(r)}
                  data-testid="workspace-search-result"
                  onClick={() => setOpen(false)}
                  className={rowClass}
                >
                  <span className="min-w-0 flex-1 truncate font-medium">{itemName(r)}</span>
                  <span className="flex-shrink-0 text-[11px] text-sage-400">{itemContext(r)}</span>
                </Link>
              ))}
            </div>
          )}

          {tasks.length > 0 && (
            <div data-testid="workspace-search-tasks">
              <div className={sectionHead}>Tasks</div>
              {tasks.map((r) => (
                <Link
                  key={r.id}
                  to={itemHref(r)}
                  data-testid="workspace-search-result"
                  onClick={() => setOpen(false)}
                  className={rowClass}
                >
                  <span className="min-w-0 flex-1 truncate font-medium">{r.title}</span>
                  <span className="flex-shrink-0 text-[11px] text-sage-400">Task</span>
                </Link>
              ))}
            </div>
          )}

          {people.length > 0 && (
            <div data-testid="workspace-search-people">
              <div className={sectionHead}>People</div>
              {people.map((m) => (
                <Link
                  key={m.user_id}
                  to="?view=caseload"
                  data-testid="workspace-search-result"
                  onClick={() => setOpen(false)}
                  className={rowClass}
                >
                  <span className="min-w-0 flex-1 truncate font-medium">{m.name}</span>
                  <span className="flex-shrink-0 text-[11px] text-sage-400">Caseload</span>
                </Link>
              ))}
            </div>
          )}

          {total === 0 && !resultsQ.isLoading && (
            <p className="px-3 py-2 text-[12.5px] text-ink-soft" data-testid="workspace-search-empty">
              Nothing in the workspace matches “{debounced}”.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
